import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { HiBookOpen, HiDownload, HiFilter } from 'react-icons/hi'
import { useLocalizedLink } from '../../hooks/useLocalizedLink'
import SEO from '../../components/SEO/SEO'
import { seoBreadcrumbPair, seoBreadcrumbTriple } from '../../utils/seoUtils'
import './Books.css'

function Books() {
  const { t, i18n } = useTranslation()
  const getLocalizedLink = useLocalizedLink()
  const { disciplineId } = useParams()
  const navigate = useNavigate()
  const [selectedCategory, setSelectedCategory] = useState(disciplineId || 'all')

  // Синхронизируем состояние с URL параметром
  useEffect(() => {
    setSelectedCategory(disciplineId || 'all')
  }, [disciplineId])
  
  const isJapanese = i18n.language === 'ja'
  const isRussian = i18n.language === 'ru'

  const getDisciplineName = (id) => {
    const names = {
      kubo: { ru: 'КУБО', en: 'KUBO', ja: 'КУБО' },
      aikido: { ru: 'Айкидо', en: 'Aikido', ja: '合気道' },
      aikijutsu: { ru: 'Айки-дзюцу', en: 'Aiki-jutsu', ja: '合気術' },
      jujutsu: { ru: 'Дзю-дзюцу', en: 'Jujutsu', ja: '柔術' },
      aikibujutsu: { ru: 'Айки-бу-дзюцу', en: 'Aiki-bu-jutsu', ja: '合気武術' },
      karatejutsu: { ru: 'Карате-дзюцу', en: 'Karate-jutsu', ja: '空手術' },
      kenjutsu: { ru: 'Кэн-дзюцу', en: 'Kenjutsu', ja: '剣術' },
      kempo: { ru: 'Кемпо', en: 'Kempo', ja: '拳法' }
    }
    if (id === 'all' || id === 'general') return t(`books.${id}`, id === 'all' ? 'All Books' : 'General')
    const lang = isJapanese ? 'ja' : (isRussian ? 'ru' : 'en')
    return names[id] ? names[id][lang] : id
  }

  const categories = [
    { id: 'all', name: t('books.all', 'All Books') },
    { id: 'kubo', name: getDisciplineName('kubo') },
    { id: 'aikido', name: getDisciplineName('aikido') },
    { id: 'aikijutsu', name: getDisciplineName('aikijutsu') },
    { id: 'jujutsu', name: getDisciplineName('jujutsu') },
    { id: 'aikibujutsu', name: getDisciplineName('aikibujutsu') },
    { id: 'karatejutsu', name: getDisciplineName('karatejutsu') },
    { id: 'kenjutsu', name: getDisciplineName('kenjutsu') },
    { id: 'kempo', name: getDisciplineName('kempo') },
    { id: 'general', name: t('books.general', 'General') }
  ]

  // Mock data - в реальном проекте будет загружаться из API
  const books = [
    {
      id: 1,
      title: isJapanese ? '50ステップで石器時代へ' : (isRussian ? '50 шагов в каменный век' : '50 Steps to the Stone Age'),
      titleRu: '50 шагов в каменный век',
      titleEn: '50 Steps to the Stone Age',
      titleJa: '50ステップで石器時代へ',
      author: isJapanese ? 'ベソノフ・パーヴェル・ガヴリロヴィチ' : (isRussian ? 'Бессонов Павел Гаврилович' : 'Bessonov Pavel Gavrilovich'),
      authorRu: 'Бессонов Павел Гаврилович',
      authorEn: 'Bessonov Pavel Gavrilovich',
      authorJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
      category: 'general',
      categoryRu: 'Общие',
      categoryEn: 'General',
      categoryJa: '一般',
      description: isJapanese ? '歴史冒険小説。15,000年前の石器時代を舞台に、3つの部族の物語を描いた作品。著者はSUMA Internationalの創設者。' : (isRussian ? 'Историко-приключенческий роман о каменном веке (15 тысяч лет до нашей эры). Повествование о трёх родовых племенах, их жизни, приключениях и выживании. Автор — основатель SUMA International.' : 'Historical adventure novel set in the Stone Age (15,000 years BC). A story about three tribal clans, their life, adventures, and survival. Author is the founder of SUMA International.'),
      descriptionRu: 'Историко-приключенческий роман о каменном веке (15 тысяч лет до нашей эры). Повествование о трёх родовых племенах, их жизни, приключениях и выживании. Автор — основатель SUMA International.',
      descriptionEn: 'Historical adventure novel set in the Stone Age (15,000 years BC). A story about three tribal clans, their life, adventures, and survival. Author is the founder of SUMA International.',
      descriptionJa: '歴史冒険小説。15,000年前の石器時代を舞台に、3つの部族の物語を描いた作品。著者はSUMA Internationalの創設者。',
      year: 2022,
      pages: 304,
      publisher: isJapanese ? '出版社「コンパス」、エカテリンブルク' : (isRussian ? 'Издательство «КОМПАС», Екатеринбург' : 'Publishing House «KOMPAS», Yekaterinburg'),
      publisherRu: 'Издательство «КОМПАС», Екатеринбург',
      publisherEn: 'Publishing House «KOMPAS», Yekaterinburg',
      publisherJa: '出版社「コンパス」、エカテリンブルク',
      isbn: '978-5-89516-329-0',
      file: '/documents/50-шагов-в-каменный-век.pdf',
      cover: '/images/books/50-shagov-v-kamennyi-vek.png',
      isFounderBook: true
    },
    {
      id: 2,
      title: isJapanese ? 'タイガーキャット' : (isRussian ? 'ТИГР-КОТ' : 'TIGER-CAT'),
      titleRu: 'ТИГР-КОТ',
      titleEn: 'TIGER-CAT',
      titleJa: 'タイガーキャット',
      subtitle: isJapanese ? '古代の伝説' : (isRussian ? 'Древнее предание' : 'Ancient Legend'),
      subtitleRu: 'Древнее предание',
      subtitleEn: 'Ancient Legend',
      subtitleJa: '古代の伝説',
      author: isJapanese ? 'ベソノフ・パーヴェル・ガヴリロヴィチ' : (isRussian ? 'Бессонов Павел Гаврилович' : 'Bessonov Pavel Gavrilovich'),
      authorRu: 'Бессонов Павел Гаврилович',
      authorEn: 'Bessonov Pavel Gavrilovich',
      authorJa: 'ベソノフ・パーヴェル・ガヴリロヴィチ',
      category: 'general',
      categoryRu: 'Общие',
      categoryEn: 'General',
      categoryJa: '一般',
      description: isJapanese ? '古代の伝説をテーマにしたファンタジー小説。白い子トラたちの物語が描かれた、神秘的な冒険物語。著者はSUMA Internationalの創設者。' : (isRussian ? 'Фантастическая повесть на основе древнего предания. История о белых тигрятах в мистическом скалистом пейзаже. Автор — основатель SUMA International.' : 'Fantasy novel based on an ancient legend. A story about white tiger cubs in a mystical rocky landscape. Author is the founder of SUMA International.'),
      descriptionRu: 'Фантастическая повесть на основе древнего предания. История о белых тигрятах в мистическом скалистом пейзаже. Автор — основатель SUMA International.',
      descriptionEn: 'Fantasy novel based on an ancient legend. A story about white tiger cubs in a mystical rocky landscape. Author is the founder of SUMA International.',
      descriptionJa: '古代の伝説をテーマにしたファンタジー小説。白い子トラたちの物語が描かれた、神秘的な冒険物語。著者はSUMA Internationalの創設者。',
      year: null,
      pages: null,
      publisher: null,
      publisherRu: null,
      publisherEn: null,
      publisherJa: null,
      isbn: null,
      file: null,
      cover: '/images/books/tigr-kot.jpg',
      isFounderBook: true
    }
  ]

  const breadcrumbItems = useMemo(() => {
    const home = t('common.home')
    const page = t('common.books', 'Books')
    if (!disciplineId || selectedCategory === 'all') {
      return seoBreadcrumbPair(home, page, 'books')
    }
    return seoBreadcrumbTriple(home, page, 'books', getDisciplineName(disciplineId), `books/${disciplineId}`)
  }, [t, disciplineId, selectedCategory, isJapanese, isRussian])

  return (
    <div className="books-page">
      <SEO 
        title={t('common.books', 'Books')}
        description={t('books.description', 'Books and publications on martial arts from SUMA International')}
        breadcrumbItems={breadcrumbItems}
      />
      <div className="container">
        <div className="books-hero">
          <HiBookOpen className="hero-icon" />
          <h1>{t('common.books', 'Books')}</h1>
          <p className="books-intro">
            {t('books.intro', 'Explore our collection of books, manuals, and publications covering various martial arts disciplines, techniques, history, and philosophy.')}
          </p>
        </div>

        <div className="books-filters">
          <HiFilter className="filter-icon" />
          <select
            value={selectedCategory}
            onChange={(e) => {
              const newCategory = e.target.value
              setSelectedCategory(newCategory)
              if (newCategory === 'all') {
                navigate(getLocalizedLink('/books'))
              } else {
                navigate(`${getLocalizedLink('/books')}/${newCategory}`)
              }
            }}
            className="category-select"
          >
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="books-list">
          {books.length > 0 ? (
            books
              .filter(book => {
                if (selectedCategory === 'all') return true
                return book.category === selectedCategory
              })
              .map(book => {
                const bookTitle = isJapanese ? (book.titleJa || book.title) : (isRussian ? (book.titleRu || book.title) : (book.titleEn || book.title))
                const bookSubtitle = book.subtitle ? (isJapanese ? (book.subtitleJa || book.subtitle) : (isRussian ? (book.subtitleRu || book.subtitle) : (book.subtitleEn || book.subtitle))) : null
                const bookAuthor = isJapanese ? (book.authorJa || book.author) : (isRussian ? (book.authorRu || book.author) : (book.authorEn || book.author))
                const bookCategory = isJapanese ? (book.categoryJa || book.category) : (isRussian ? (book.categoryRu || book.category) : (book.categoryEn || book.category))
                const bookDescription = isJapanese ? (book.descriptionJa || book.description) : (isRussian ? (book.descriptionRu || book.description) : (book.descriptionEn || book.description))
                const bookPublisher = book.publisher ? (isJapanese ? (book.publisherJa || book.publisher) : (isRussian ? (book.publisherRu || book.publisher) : (book.publisherEn || book.publisher))) : null
                
                return (
                  <div key={book.id} className={`book-card ${book.isFounderBook ? 'book-founder' : ''}`}>
                    {book.cover ? (
                      <div className="book-cover">
                        <img src={book.cover} alt={bookTitle} />
                      </div>
                    ) : (
                      <div className="book-cover-placeholder">
                        <HiBookOpen className="book-icon" />
                      </div>
                    )}
                    <div className="book-info">
                      <h3>{bookTitle}</h3>
                      {bookSubtitle && (
                        <p className="book-subtitle">{bookSubtitle}</p>
                      )}
                      <p className="book-author">{bookAuthor}</p>
                      {book.isFounderBook && (
                        <p className="book-founder-badge">{t('books.founderBook', 'Книга основателя SUMA')}</p>
                      )}
                      <p className="book-category">{bookCategory}</p>
                      <p className="book-description">{bookDescription}</p>
                      {(book.year || book.pages || bookPublisher || book.isbn) && (
                        <div className="book-meta">
                          {book.year && (
                            <span className="book-year">{book.year}</span>
                          )}
                          {book.pages && (
                            <span className="book-pages">{book.pages} {t('books.pages', 'стр.')}</span>
                          )}
                          {bookPublisher && (
                            <span className="book-publisher">{bookPublisher}</span>
                          )}
                          {book.isbn && (
                            <span className="book-isbn">ISBN: {book.isbn}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="book-actions">
                      {book.file ? (
                        <a href={book.file} download className="btn btn-primary">
                          <HiDownload className="btn-icon" />
                          {t('books.download', 'Скачать')}
                        </a>
                      ) : (
                        <span className="book-coming-soon">{t('books.comingSoon', 'Скоро')}</span>
                      )}
                      {book.preview && (
                        <button className="btn btn-secondary">
                          {t('books.preview', 'Просмотр')}
                        </button>
                      )}
                    </div>
                  </div>
                )
              })
          ) : (
            <div className="no-books">
              <HiBookOpen className="no-books-icon" />
              <p>{t('books.noBooks', 'Книги будут отображаться здесь по мере их добавления в коллекцию.')}</p>
            </div>
          )}
        </div>

        <section className="books-info">
          <h2>{t('books.publicationInfo', 'Publication Information')}</h2>
          <p>
            {t('books.publicationInfoText', 'SUMA International publishes books and educational materials to support the development and understanding of martial arts. For information about publishing or purchasing books, please contact us.')}
          </p>
          <div className="books-actions">
            <Link to={getLocalizedLink('/contact')} className="btn btn-primary">
              {t('common.contact')}
            </Link>
            <Link to={getLocalizedLink('/documents')} className="btn btn-secondary">
              {t('common.documents')}
            </Link>
          </div>
        </section>
      </div>
    </div>
  )
}

export default Books

